import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  EnvelopeIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { withShopifyCtx } from '@/utils/shopifyNavigation';
import { useCompany } from '@/utils/CompanyContext';
import ONBOARDING_COACH_CONTENT from './onboardingCoachContent';

const COACH_STORAGE_KEY = 'afivio:onboarding-coach:auto-open-disabled';

export const OnboardingCoach = () => {
  const router = useRouter();
  const { activeCompany, userCompanyDetails } = useCompany();
  const content = ONBOARDING_COACH_CONTENT[router.pathname];
  const isRedirectScreen =
    router.pathname === '/dashboard' ||
    router.pathname === '/dashboard/[companyId]' ||
    router.pathname === '/dashboard/[companyId]/setup';
  const [isReady, setIsReady] = useState(false);
  const [isAutoOpenDisabled, setIsAutoOpenDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    try {
      const autoOpenDisabled =
        window.localStorage.getItem(COACH_STORAGE_KEY) === 'true' ||
        window.localStorage.getItem('afivio:onboarding-coach:skipped') === 'true';
      setIsAutoOpenDisabled(autoOpenDisabled);
      setIsOpen(!autoOpenDisabled);
    } catch {
      setIsAutoOpenDisabled(false);
    }
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    setIsClosing(false);
    setIsOpen(!isAutoOpenDisabled);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [isAutoOpenDisabled, router.pathname]);

  const hasCompletedSetup =
    activeCompany?.domain_verified === true || activeCompany?.skip_verified === true;
  const isFinalScreen =
    router.pathname === '/dashboard/[companyId]/setup/skip-complete' ||
    (router.pathname === '/dashboard/[companyId]/setup/verify' && hasCompletedSetup);

  // The company context is populated asynchronously after login. Until it has
  // finished loading, `activeCompany` is undefined even for an already set up
  // company, which used to make the coach briefly appear before the dashboard
  // redirect completed.
  const isWaitingForCompanyData = userCompanyDetails === null;

  if (
    !isReady ||
    !content ||
    isRedirectScreen ||
    isFinalScreen ||
    hasCompletedSetup ||
    isWaitingForCompanyData
  ) return null;

  const collapse = () => {
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 180);
  };

  const skipGuide = () => {
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(COACH_STORAGE_KEY, 'true');
        window.localStorage.removeItem('afivio:onboarding-coach:skipped');
      } catch {
        // Auto-opening still stays disabled for this session if storage is unavailable.
      }
      setIsAutoOpenDisabled(true);
      setIsOpen(false);
      setIsClosing(false);
    }, 180);
  };

  const expand = () => {
    setIsClosing(false);
    setIsOpen(true);
  };

  const openSupportChat = () => {
    document.body.classList.remove('smartsupp-hidden');
    let attempts = 0;
    const maxAttempts = 24;

    const openChat = () => {
      if (typeof window.smartsupp !== 'function') return false;
      window.smartsupp('chat:show');
      window.smartsupp('chat:open');
      return true;
    };

    if (openChat()) return;

    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (openChat() || attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 250);
  };

  const howItWorksHref = withShopifyCtx(
    `/dashboard/how-it-work?returnTo=${encodeURIComponent(router.asPath)}`,
    router
  );

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      {isOpen && (
        <aside
          key={router.pathname}
          className={`coach-bubble pointer-events-auto relative w-[21.5rem] max-w-[calc(100vw-2rem)] rounded-3xl border border-slate-200/90 bg-white/95 text-slate-900 shadow-[0_24px_70px_-24px_rgba(15,23,42,0.35)] backdrop-blur-xl ${
            isClosing ? 'coach-bubble--closing' : ''
          }`}
          aria-labelledby="onboarding-coach-title"
          aria-live="polite"
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" aria-hidden="true">
            <span className="absolute -right-12 -top-16 h-36 w-36 rounded-full bg-blue-100/70 blur-3xl" />
            <span className="absolute -left-12 bottom-0 h-28 w-28 rounded-full bg-violet-100/60 blur-3xl" />
          </div>

          <div className="relative p-4">
            <div className="flex items-start gap-3">
              <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-600/20">
                <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                <span className="coach-status-dot absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full border-[3px] border-white bg-emerald-500" />
              </div>

              <div className="min-w-0 flex-1">
                <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700">
                  {content.phase}
                </span>
                <h2 id="onboarding-coach-title" className="mt-2 text-base font-bold leading-snug text-slate-950">
                  {content.title}
                </h2>
              </div>

              <button
                type="button"
                onClick={collapse}
                className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Minimize setup guide"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <p className="mt-3 text-sm leading-6 text-slate-600">
              {content.description}
            </p>

            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/90 p-2.5">
              <p className="px-1 text-[11px] leading-4 text-slate-500">
                More details are available in Dashboard → How It Works.
              </p>
              <div className="mt-2 grid grid-cols-3 gap-1.5">
                <a
                  href={howItWorksHref}
                  className="group flex min-h-[48px] flex-col items-center justify-center gap-1 rounded-xl bg-white px-2 py-1.5 text-center text-[11px] font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <BookOpenIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />
                  How it works
                </a>
                <button
                  type="button"
                  onClick={openSupportChat}
                  className="flex min-h-[48px] flex-col items-center justify-center gap-1 rounded-xl bg-white px-2 py-1.5 text-[11px] font-semibold text-blue-700 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:ring-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden="true" />
                  Chat
                </button>
                <a
                  href="mailto:contact@afivio.com?subject=Afivio%20onboarding%20help"
                  className="flex min-h-[48px] flex-col items-center justify-center gap-1 rounded-xl bg-white px-2 py-1.5 text-center text-[11px] font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:ring-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />
                  Email
                </a>
              </div>
              <p className="mt-2 text-center text-[10px] font-medium text-emerald-700">
                Chat and email setup help are free.
              </p>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={skipGuide}
                className="min-h-[44px] rounded-xl px-2 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Stop opening the setup guide automatically on the next screens"
              >
                Skip guide
              </button>
              <button
                type="button"
                onClick={collapse}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                Got it
              </button>
            </div>
          </div>

          <span
            className="absolute -bottom-2 right-5 h-4 w-4 rotate-45 border-b border-r border-slate-200 bg-white"
            aria-hidden="true"
          />
        </aside>
      )}

      <button
        type="button"
        onClick={isOpen ? collapse : expand}
        className="coach-launcher pointer-events-auto group relative flex h-14 w-14 items-center justify-center rounded-full border border-white bg-white text-blue-700 shadow-[0_14px_35px_-12px_rgba(37,99,235,0.65)] transition hover:-translate-y-1 hover:shadow-[0_18px_42px_-12px_rgba(37,99,235,0.75)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label={isOpen ? 'Minimize setup guide' : 'Show setup guide'}
        aria-expanded={isOpen}
      >
        <span className="absolute inset-1 rounded-full bg-gradient-to-br from-blue-50 to-indigo-100" aria-hidden="true" />
        <span className="coach-launcher__pulse absolute inset-0 rounded-full ring-2 ring-blue-400/40" aria-hidden="true" />
        <SparklesIcon className="relative h-6 w-6 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" aria-hidden="true" />
        {!isOpen && (
          <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-[3px] border-white bg-emerald-500" />
        )}
      </button>

      <style jsx>{`
        .coach-bubble {
          transform-origin: bottom right;
          animation: coach-enter 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .coach-bubble--closing {
          animation: coach-exit 180ms ease-in both;
        }

        .coach-launcher {
          animation: coach-float 4s ease-in-out infinite;
        }

        .coach-launcher:hover {
          animation-play-state: paused;
          transform: translateY(-4px) scale(1.03);
        }

        .coach-launcher__pulse {
          animation: coach-pulse 2.6s ease-out infinite;
        }

        .coach-status-dot {
          animation: coach-status 2s ease-in-out infinite;
        }

        @keyframes coach-enter {
          from {
            opacity: 0;
            transform: translateY(16px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes coach-exit {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(8px) scale(0.97);
          }
        }

        @keyframes coach-float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        @keyframes coach-pulse {
          0% {
            opacity: 0.55;
            transform: scale(1);
          }
          70%,
          100% {
            opacity: 0;
            transform: scale(1.25);
          }
        }

        @keyframes coach-status {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.55;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .coach-bubble,
          .coach-bubble--closing,
          .coach-launcher,
          .coach-launcher__pulse,
          .coach-status-dot {
            animation: none;
          }

          .coach-launcher {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingCoach;
